import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type { Sponsor, SponsorLayout } from "src/types";

import TopLevelFloatingOptions from "@/components/editor/block-outline/floating-options/top-level-floating-component";
import Text from "@/components/editor/primitives/text/text-component";
import {
  BlockBackgroundColorWrapper,
  BlockMaxWidthWrapper,
  BlockPaddingWrapper,
} from "@/components/editor/shared/block-wrapper";
import SyncedSponsorsSettings from "@/components/editor/shared/sponsors/sponsors-settings/synced-sponsors-settings-component";
import Element from "@/components/element/element-component";
import {
  getSponsorsByTier,
  getSponsorTitleByLayout,
} from "@/features/sponsors/sponsors-reducer";
import { getTextCustomizations } from "@/features/text-customizations/text-customizations-reducer";

import SponsorCard from "./sponsor-card/sponsor-card-component";
import SponsorsList from "./sponsors-list-component";

type SyncedSponsorsProps = {
  sponsors?: Sponsor[];
  title: string;
  layout: SponsorLayout;
};

export default function SyncedSponsors(props: SyncedSponsorsProps) {
  const {
    connectors: { connect },
    id: nodeId,
  } = useNode();
  const dashboardSponsors = useSelector(getSponsorsByTier(props.layout));
  const sponsors = props.sponsors ?? dashboardSponsors;
  const { t } = useTranslation();
  const textCustomizations = useSelector(getTextCustomizations);

  const isEmpty = sponsors.length === 0;
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));

  if (!enabled && isEmpty) return null;

  return (
    <BlockBackgroundColorWrapper ref={connect} id={nodeId}>
      <BlockPaddingWrapper>
        <BlockMaxWidthWrapper>
          <div className="flex justify-center">
            <Element
              id="sponsor-title"
              is={Text}
              element="h2"
              text={getSponsorTitleByLayout({
                t,
                layout: props.layout,
                textCustomizations,
              })}
              pattern="headingTwo"
            />
          </div>
          <SponsorsList layout={props.layout}>
            {enabled && isEmpty && <SyncedSponsorsSettings minimal />}
            {sponsors.map((sponsor) => (
              <SponsorCard key={sponsor.id + sponsor.name} sponsor={sponsor} />
            ))}
          </SponsorsList>
        </BlockMaxWidthWrapper>
      </BlockPaddingWrapper>
    </BlockBackgroundColorWrapper>
  );
}

SyncedSponsors.craft = {
  custom: {
    displayName: "Synced Sponsors",
  },
  related: {
    leftOutlineOptions: TopLevelFloatingOptions,
    settings: SyncedSponsorsSettings,
  },
};
